import { Dispatch } from 'redux';
import AuthenticatedController from '../controllers/AuthenticatedController';
import UsersController from '../controllers/UsersController';
import commonHandler from '../errorHandlers/common-handler';
import { userHandler } from '../errorHandlers/user-handler';
import CompanyUsersModel, { UserAttributes } from '../model/CompanyUsersModel';
import { instachampEnrollmentsSelector } from '../selectors/userSelector';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';
import subscriptionCRUDActions from './SubscriptionCRUDActions';
import { resetCompany } from './commonActions';

type RetrieveById = (
  companyId: number,
  userId: number,
) => (dispatch: Dispatch<StoreState>) => Promise<CompanyUsersModel>;

type RetrieveAll = (
  companyId: number,
  pageNumber?: number,
  pageSize?: number,
  query?: string,
) => (dispatch: Dispatch<StoreState>) => Promise<void>;

type RetrieveAllByFacebookPage = (
  facebookId: number,
  companyId: number,
  pageNumber: number,
  pageSize: number,
) => (dispatch: Dispatch<StoreState>) => Promise<CompanyUsersModel[]>;

type Update = (
  companyId: number,
  entity: CompanyUsersModel,
  retrievePagination?: {
    pageNumber?: number;
    pageSize?: number;
  },
) => (dispatch: Dispatch<StoreState>) => Promise<CompanyUsersModel>;

type UpdateUserAttributes = (
  companyId: number | undefined,
  entityId: number,
  userAttributes: Partial<UserAttributes>,
  retrieveUsersAfterUpdate?: boolean,
) => (dispatch: Dispatch<StoreState>) => Promise<CompanyUsersModel>;

type Delete = (
  companyId: number,
  entityId: number,
) => (dispatch: Dispatch<StoreState>) => Promise<{ succes: boolean }>;

class UsersCRUDActions extends CrudActionContainer {
  updateUserAttributesSessionAction: string;
  retriveAllByFacebookPageAction: string;
  updatePapAffiliateAction: string;
  retrievedByIdAction: string;

  constructor() {
    super('USERS');
    this.updateUserAttributesSessionAction = this.makeActionKey(
      'UPDATE_USER_ATTRIBUTES_SESSION',
    );
    this.retriveAllByFacebookPageAction = this.makeActionKey(
      'RETRIVE_ALL_BY_FACEBOOK_PAGE',
    );
    this.updatePapAffiliateAction = this.makeActionKey('UPDATE_PAP_AFFILIATE');
    this.retrievedByIdAction = this.makeActionKey('RETRIVE__BY_ID');
  }

  retrieveById: RetrieveById = (companyId, userId) => async dispatch => {
    try {
      const response = await UsersController.getUserById(companyId, userId);
      dispatch({
        type: this.retrievedByIdAction,
        user: response,
      });
      return response;
    } catch (e) {
      await commonHandler(dispatch)(e);
      throw e;
    }
  };

  retrieveAll: RetrieveAll =
    (companyId, pageNumber, pageSize, query) => async dispatch => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const { entities, pagination } = await UsersController.getAllByCompany(
          companyId,
          pageNumber,
          pageSize,
          query,
        );
        dispatch(this.retrievedAllPagination(entities, pagination));
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };

  retriveAllByFacebookPage: RetrieveAllByFacebookPage =
    (facebookPageId, companyId, pageNumber = 1, pageSize = 25) =>
    async dispatch => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const response = await UsersController.getAllByFBPage(
          facebookPageId,
          companyId,
          pageNumber,
          pageSize,
        );
        dispatch({
          type: this.retriveAllByFacebookPageAction,
          users: response,
        });
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };

  update: Update =
    (
      companyId,
      entity,
      retrievePagination = {
        pageNumber: 1,
        pageSize: 25,
      },
    ) =>
    async dispatch => {
      dispatch(this.busy(this.updateAction));
      try {
        const response = await UsersController.updateCompanyUser(
          companyId,
          entity.id,
          entity,
        );
        dispatch(
          this.retrieveAll(
            companyId,
            retrievePagination.pageNumber,
            retrievePagination.pageSize,
          ),
        );
        return response;
      } catch (e) {
        await userHandler(dispatch)(e, this.updateAction);
        throw e;
      }
    };

  updateById = (
    companyId: number,
    userId: number,
    update: Partial<CompanyUsersModel>,
  ) =>
    this.update(companyId, {
      id: userId,
      ...update,
    } as CompanyUsersModel);

  updateCurrentUserAttributes =
    (updatedAttributes: Partial<UserAttributes>) =>
    async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
      const state = getState();

      return dispatch(
        this.updateUserAttributes(
          state.session.session.userAttributes.activeCompanyId,
          state.session.session.id,
          updatedAttributes,
        ),
      );
    };

  updateUserAttributes: UpdateUserAttributes =
    (
      companyId,
      entityId,
      userAttributes,
      retrieveUsersAfterUpdate = true,
      dispatchBusy = true,
    ) =>
    async dispatch => {
      if (dispatchBusy) {
        dispatch(this.busy(this.updateAction));
      }
      try {
        const response = await UsersController.updateUserAttributes(
          entityId,
          userAttributes,
        );

        dispatch({
          type: this.updateUserAttributesSessionAction,
          payload: { userAttributes },
        });

        const oldSession = AuthenticatedController.getSession();

        if (oldSession) {
          AuthenticatedController.storeSession({
            ...oldSession,
            userAttributes: {
              ...oldSession.userAttributes,
              ...userAttributes,
            },
          });
        }

        if (companyId && retrieveUsersAfterUpdate) {
          dispatch(this.retrieveAll(companyId));
        } else {
          dispatch(this.unbusy());
        }
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
        throw e;
      }
    };

  updateCurrentUserActiveCompanyId =
    (activeCompanyId: number) =>
    async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
      const state = getState();

      return dispatch(
        this.updateActiveCompanyId(state.session.session.id, activeCompanyId),
      );
    };

  updateActiveCompanyId =
    (entittyId: number, activeCompanyId: number) =>
    async (dispatch: Dispatch<StoreState>) => {
      dispatch(resetCompany());
      dispatch(this.busy(this.updateAction));
      try {
        const userAttributes = { activeCompanyId };

        const response = await UsersController.updateUserAttributes(
          entittyId,
          userAttributes,
        );

        dispatch({
          type: this.updateUserAttributesSessionAction,
          payload: { userAttributes },
        });

        const oldSession = AuthenticatedController.getSession();

        if (oldSession) {
          AuthenticatedController.storeSession({
            ...oldSession,
            userAttributes: {
              ...oldSession.userAttributes,
              ...userAttributes,
            },
          });
        }

        dispatch(this.unbusy());

        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };

  updateActiveChatbotId =
    (entityId: number, activeChatbotId: number) =>
    async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        const userAttributes = { activeChatbotId };

        const response = await UsersController.updateUserAttributes(
          entityId,
          userAttributes,
        );

        dispatch({
          type: this.updateUserAttributesSessionAction,
          payload: { userAttributes },
        });

        const oldSession = AuthenticatedController.getSession();
        if (oldSession) {
          AuthenticatedController.storeSession({
            ...oldSession,
            userAttributes: {
              ...oldSession.userAttributes,
              ...userAttributes,
            },
          });
        }

        dispatch(this.unbusy());

        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };

  delete: Delete = (companyId, entityId) => async dispatch => {
    dispatch(this.busy(this.deleteAction));
    try {
      const response = await UsersController.deleteCompanyUser(
        companyId,
        entityId,
      );
      dispatch(subscriptionCRUDActions.getAccountData(companyId));
      dispatch(this.retrieveAll(companyId));
      return response;
    } catch (e) {
      commonHandler(dispatch)(e);
    }
  };

  requestDemo =
    (companyId: number) => async (dispatch: Dispatch<StoreState>) => {
      try {
        return await UsersController.requestDemo(companyId);
      } catch (e) {
        await commonHandler(dispatch)(e);
      }
    };

  logInstachampEnrollment =
    (instagramAccount: string) =>
    async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
      const instachampEnrollments = {
        ...instachampEnrollmentsSelector(getState()),
        [instagramAccount]: Date.now(),
      };

      return dispatch(
        this.updateCurrentUserAttributes({ instachampEnrollments }),
      );
    };

  getUserPapAffiliate =
    () =>
    async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
      const userId = getState().session.session.id;
      let papAffiliate;
      try {
        papAffiliate = await UsersController.getUserPapAffiliate(userId);
      } catch {
        papAffiliate = null;
      }
      dispatch({
        type: this.updatePapAffiliateAction,
        papAffiliate,
      });
      return papAffiliate;
    };

  updateUserPapAffiliate =
    (params: { referralid: string; paypalEmail: string; username: string }) =>
    async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
      const userId = getState().session.session.id;
      const result = await UsersController.putUserPapAffiliate(userId, params);
      if (result.success) {
        dispatch({
          type: this.updatePapAffiliateAction,
          papAffiliate: result.papAffiliate,
        });
      }
      return result;
    };
}

const usersCRUDActions = new UsersCRUDActions();

export const useUsersCRUDActions = getActionHookBuilder(usersCRUDActions, [
  'retrieveById',
  'retrieveAll',
  'updateUserAttributes',
  'updateCurrentUserAttributes',
  'update',
  'updateById',
  'updateActiveCompanyId',
  'updateCurrentUserActiveCompanyId',
  'logInstachampEnrollment',
  'getUserPapAffiliate',
  'updateUserPapAffiliate',
  'delete',
]);

export default usersCRUDActions;
